/* ------------------------------------------------------------------
   ChatBot.js   Greatly Social – v2.3 (Daily Feedback Update)
   - Modern UI/UX inspired by Intercom/Drift
   - Animated launcher button (open/close)
   - "Fork in the road" mode selection
   - Listener for external "openChatToFeedback" events
   - Built-in Daily Feedback Prompt logic (Updated from Weekly)
------------------------------------------------------------------- */

import React, { useState, useEffect, useRef } from 'react';
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import './ChatBot.css'; // Make sure this is imported after the library styles
import axios from 'axios';
import { v4 as uuid } from 'uuid';

/* ---------- constants ---------- */
const CHAT_URL    = '/api/gschat'; 
const BOT_AVATAR  = '/assets/logo/icon-nobackground.webp';
const USER_AVATAR = '/assets/logo/icon-nobackground.webp';
const HEADER_ICON = '/assets/logo/icon-nobackground.webp';

/* ---------- Sanitize Helpers ---------- */
const sanitize = s => s.replace(/&nbsp;|\u00A0/g, ' ');
const mdBold = s => sanitize(s).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

/* ---------- SVG Icons ---------- */
const ChatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);

const BackIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
);

export default function ChatBot() {
  const [messages, setMessages] = useState([]);
  const [isTyping, setTyping]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [chatMode, setChatMode] = useState(null); 
  const [showWeeklyToast, setShowWeeklyToast] = useState(false);
  const messageListRef          = useRef(null);

  /* Auto-scroll to the bottom when new messages arrive */
  useEffect(() => {
    if (messageListRef.current) {
        setTimeout(() => {
            messageListRef.current.scrollToBottom('smooth');
        }, 100);
    }
  }, [messages, isTyping, chatMode]);

  /* ────────── LISTENER & DAILY PROMPT LOGIC ────────── */
  useEffect(() => {
    // 1. Listen for clicks from the FeedbackPopup or Header
    const handleExternalFeedbackRequest = () => {
      setOpen(true);
      handleStartChat('feedback');
    };

    // 2. DAILY Prompt Logic (Updated from oneWeek to oneDay)
    const lastPrompt = localStorage.getItem('gs_lastFeedbackPrompt');
    const oneDay = 1 * 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    const toastTimer = setTimeout(() => {
      if (!lastPrompt || (Date.now() - parseInt(lastPrompt)) > oneDay) {
        setShowWeeklyToast(true);
      }
    }, 15000); // Wait 15s before prompting

    window.addEventListener('openChatToFeedback', handleExternalFeedbackRequest);
    
    return () => {
      window.removeEventListener('openChatToFeedback', handleExternalFeedbackRequest);
      clearTimeout(toastTimer);
    };
  }, []);

  const handleToastAction = () => {
    setShowWeeklyToast(false);
    localStorage.setItem('gs_lastFeedbackPrompt', Date.now().toString());
    setOpen(true);
    handleStartChat('feedback');
  };

  /* ───────────────────────────────────────────────────────── */

  const handleStartChat = (mode) => {
    setChatMode(mode);
    const greeting = mode === 'support' 
      ? "Hey there 👋 How can I help you with Greatly Social today?" 
      : "Awesome! 💡 What feature or idea would you like to see added to Greatly Social?";

    setMessages([
      {
        id: uuid(),
        html: mdBold(greeting),
        sender: 'bot',
        direction: 'incoming',
        avatar: BOT_AVATAR,
      },
    ]);
  };

  const handleResetMode = () => {
    setChatMode(null);
    setMessages([]);
  };

  async function handleSend(text) {
    const cleanText = sanitize(text);
    if (!cleanText.trim()) return;

    const userMsg = {
      id: uuid(),
      text: cleanText,
      sender: 'user',
      direction: 'outgoing',
      avatar: USER_AVATAR,
    };
    setMessages(m => [...m, userMsg]);
    setTyping(true);

    const history = [...messages, userMsg]
      .filter(m => m.sender) 
      .slice(-10)
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.html || m.text, 
      }));

    try {
      const { data } = await axios.post(
        CHAT_URL,
        { message: cleanText, history, mode: chatMode }, 
        { headers: { 'Content-Type': 'application/json' } }
      );

      setMessages(m => [
        ...m,
        {
          id: uuid(),
          html: mdBold(data.reply || '…'), 
          sender: 'bot',
          direction: 'incoming',
          avatar: BOT_AVATAR,
        },
      ]);
    } catch (err) {
      console.error('[ChatBot] API Error:', err);
      setMessages(m => [
        ...m,
        {
          id: uuid(),
          html: mdBold("🙈 Oops! Something went wrong. Please try again later."),
          sender: 'bot',
          direction: 'incoming',
          avatar: BOT_AVATAR,
        },
      ]);
    } finally {
      setTyping(false);
    }
  }

  const forkStyles = {
    container: { display: 'flex', flexDirection: 'column', height: '100%', padding: '24px', justifyContent: 'center', backgroundColor: '#fff', textAlign: 'center' },
    title: { fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', color: '#111827' },
    subtitle: { fontSize: '14px', color: '#4B5563', marginBottom: '24px' },
    btnSupport: { padding: '12px 16px', backgroundColor: '#007BFF', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', marginBottom: '12px', transition: 'opacity 0.2s' },
    btnFeedback: { padding: '12px 16px', backgroundColor: '#F3F4F6', color: '#111827', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s' },
  };

  return (
    <>
      {/* Daily Feedback Toast */}
      {showWeeklyToast && !open && (
        <div 
          className="gs-feedback-toast"
          style={{
            position: 'fixed',
            bottom: '85px',
            right: '20px',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            padding: '16px',
            width: '280px',
            zIndex: 9999,
            border: '1px solid #E5E7EB'
          }}
        >
          <button 
            onClick={() => setShowWeeklyToast(false)} 
            style={{ position: 'absolute', top: '8px', right: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}
          >✕</button>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#111827', marginBottom: '4px' }}>Got a quick idea? 💡</div>
          <div style={{ fontSize: '13px', color: '#4B5563', marginBottom: '12px' }}>How can we make Greatly Social better for you?</div>
          <button onClick={handleToastAction} style={{ width: '100%', padding: '8px', backgroundColor: '#007BFF', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Suggest a Feature</button>
        </div>
      )}

      <button
        onClick={() => {
            setOpen(!open);
            if (showWeeklyToast) setShowWeeklyToast(false);
        }}
        className={`gs-chat-launcher ${open ? 'open' : ''}`}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        <div className="gs-chat-launcher-icon open-icon"><ChatIcon /></div>
        <div className="gs-chat-launcher-icon close-icon"><CloseIcon /></div>
      </button>

      <div className={`gs-chat-wrapper ${open ? 'open' : ''}`}>
        <div className="gs-chat-header">
            <div className="gs-chat-header-content">
                {chatMode && (
                    <button 
                        onClick={handleResetMode} 
                        style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', marginRight: '10px', padding: 0, display: 'flex', alignItems: 'center' }}
                    >
                        <BackIcon />
                    </button>
                )}
                <img src={HEADER_ICON} alt="Greatly Social" className="gs-chat-header-logo" />
                <div>
                    <div className="gs-chat-header-title">
                        {!chatMode ? "How can we help?" : chatMode === 'support' ? "Greatly Support" : "Feature Requests"}
                    </div>
                    <div className="gs-chat-header-subtitle">We typically reply in a minute.</div>
                </div>
            </div>
            <button onClick={() => setOpen(false)} className="gs-chat-close-btn" aria-label="Close chat">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            </button>
        </div>

        <MainContainer style={{ flex: 1, border: 'none', backgroundColor: '#fff' }}>
          {!chatMode ? (
            <div style={forkStyles.container}>
                <div style={forkStyles.title}>Welcome to Greatly Social</div>
                <div style={forkStyles.subtitle}>Choose an option below to get started.</div>
                <button style={forkStyles.btnSupport} onClick={() => handleStartChat('support')}>💬 Get Support</button>
                <button style={forkStyles.btnFeedback} onClick={() => handleStartChat('feedback')}>💡 Suggest a Feature</button>
            </div>
          ) : (
            <ChatContainer>
              <MessageList
                ref={messageListRef}
                typingIndicator={isTyping ? <TypingIndicator content="Greatly AI is typing…" /> : null}
              >
                {messages.map((m) => (
                  <Message
                    key={m.id}
                    model={{ direction: m.direction, type: m.html ? 'html' : 'text', payload: m.html || m.text, sender: m.sender }}
                  >
                    <Message.Avatar src={m.avatar} name={m.sender} />
                  </Message>
                ))}
              </MessageList>
              <MessageInput placeholder="Type here…" onSend={handleSend} attachButton={false} sendButton={true} autoFocus />
            </ChatContainer>
          )}
        </MainContainer>
      </div>
    </>
  );
}